import * as RoutesModule from 'Routes';

import { createContext, useContext } from 'react';

import App from './App';
import { AppErrorBoundary } from 'components/ui/AppErrorBoundary';
import { AuthenticationService } from 'services/AuthenticationService';
import { AxiosErrorHandler } from 'services/AxiosErrorHandler';
import { AxiosInstance } from 'axios';
import { AxiosServiceConfig } from 'services/AxiosServiceConfig';
import { AxiosServiceFactory } from 'services/AxiosServiceFactory';
import { BrowserRouter } from 'react-router-dom';
import { ConfigurationService } from 'services/ConfigurationService';
import { InviteStore } from 'stores/InviteStore';
import { LanguageStore } from 'stores/LanguageStore';
import { NotificationsStore } from 'stores/NotificationsStore';
import { ProfileStore } from 'stores/ProfileStore';
import ReactDOM from 'react-dom/client';
import { ServiceTypes } from 'utilities/Constants';
import StartupService from 'services/StartupService';
import StartupStore from 'stores/StartupStore';
import { UsersStore } from 'stores/UsersStore';
import registerServiceWorker from './registerServiceWorker';
import {ClientsStore} from "./stores/ClientsStore";


const rootElement = document.getElementById('root');
const root = ReactDOM.createRoot(rootElement!);

registerServiceWorker();

type Stores = {
  errorHandler: AxiosErrorHandler;
  configurationService: ConfigurationService;
  authenticationService: AuthenticationService;
  teamPortalApiAxiosInstance: AxiosInstance;
  notificationStore: NotificationsStore;
  profileStore: ProfileStore;
  languageStore: LanguageStore;
  inviteStore: InviteStore;
  usersStore: UsersStore;
  startupStore: StartupStore;
  clientStore: ClientsStore;
};

const StoresContext = createContext<Stores | null>(null);

export const useStores = () => {
  const stores = useContext(StoresContext);
  if (!stores) {
    throw new Error('useStores must be used within a StoreProvider.');
  }
  return stores;
};

async function RenderApp() {
  const errorHandler = new AxiosErrorHandler();
  const configurationService = new ConfigurationService();
  const authenticationService = new AuthenticationService();
  const serviceFactory = new AxiosServiceFactory(configurationService);
  const teamPortalApiAxiosInstance = await serviceFactory.createInstance(ServiceTypes.apiServiceAxiosInstance);
  const notificationStore = new NotificationsStore();
  const profileStore = new ProfileStore(notificationStore);
  const languageStore = new LanguageStore(profileStore);
  const usersStore = new UsersStore(profileStore, notificationStore);
  const inviteStore = new InviteStore(notificationStore, usersStore);
  const clientStore = new ClientsStore(notificationStore)

  const startupService = new StartupService(teamPortalApiAxiosInstance);
  const startupStore = new StartupStore(
    authenticationService,
    configurationService,
    profileStore,
    languageStore,
    startupService,
    notificationStore
  );

  const stores: Stores = {
    errorHandler,
    configurationService,
    authenticationService,
    teamPortalApiAxiosInstance,
    notificationStore,
    profileStore,
    languageStore,
    inviteStore,
    usersStore,
    clientStore,
    startupStore,
  };

  AxiosServiceConfig.setDefaultDecorators(teamPortalApiAxiosInstance, authenticationService, errorHandler);

  root.render(
    <AppErrorBoundary>
      <BrowserRouter>
        <StoresContext.Provider value={stores}>
          <App>{RoutesModule.routes}</App>
        </StoresContext.Provider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

RenderApp();
